/**
 * Platform abstraction: one place that resolves getPlatform(): 'android' | 'web'
 * and exposes storage, deviceId, app, updater, watchdog, keepAwake, openSystemSettings.
 * Implementations: web (localStorage, no-ops), Tauri Android (invoke to Rust).
 *
 * Platform is resolved in initPlatform() via the Tauri OS plugin (works when
 * `window.__TAURI__` is unset — Tauri v2 often uses `isTauri` / `__TAURI_INTERNALS__` only). Must call await initPlatform()
 * before using getPlatformInstance() or platform APIs on Android.
 */

import type { IPlatform, PlatformKind, PlayerPlatformHeader } from './types'
import { webPlatform } from './web'

let platformInstance: IPlatform = webPlatform
/** Resolved in initPlatform() from @tauri-apps/plugin-os; default 'web' until then. */
let resolvedPlatform: PlatformKind = 'web'
/** Value for x-player-platform header; set in initPlatform(). */
let playerPlatformHeader: PlayerPlatformHeader = 'web'
let platformReady = false
let resolvePlatformReady!: () => void
const platformReadyPromise = new Promise<void>((resolve) => {
  resolvePlatformReady = resolve
})

export function getPlatform(): PlatformKind {
  if (typeof window === 'undefined') { return 'web' }
  return resolvedPlatform
}

/** Value to send as x-player-platform to the API. Use after initPlatform() has run. */
export function getPlayerPlatformHeader(): PlayerPlatformHeader {
  if (typeof window === 'undefined') { return 'web' }
  return playerPlatformHeader
}

export function getPlatformInstance(): IPlatform {
  return platformInstance
}

export function isPlatformReady(): boolean {
  return platformReady
}

/** Resolves after `initPlatform()` finishes. Waiters may register before `initPlatform()` is called. */
export function whenPlatformReady(): Promise<void> {
  return platformReadyPromise
}

/**
 * Call once at app startup. Uses the OS plugin to detect platform (android vs everything else),
 * then loads the Tauri implementation when on Android. Must be awaited before using
 * platform APIs on Android.
 */
export async function initPlatform(): Promise<void> {
  try {
    try {
      const { platform: getOsPlatform } = await import('@tauri-apps/plugin-os')
      const os = await getOsPlatform()
      resolvedPlatform = os === 'android' ? 'android' : 'web'
      playerPlatformHeader = os === 'android' ? 'android' : 'web'
    }
    catch {
      resolvedPlatform = 'web'
      playerPlatformHeader = 'web'
    }
    if (resolvedPlatform === 'android') {
      const { tauriPlatform } = await import('./tauri')
      platformInstance = tauriPlatform
      await tauriPlatform.requestFullscreen?.().catch(() => {})
      const { initAndroidTauriKioskUx } = await import('./tauriAndroidLifecycle')
      await initAndroidTauriKioskUx().catch(() => {})
      // Hard kiosk (Lock Task) on Device Owner screens, gated on Studio's policy from the last
      // persisted registration snapshot (defaults to on before any snapshot exists). Never throws.
      const { bootstrapAndroidKioskLockAtStartup } = await import('@/composables/useKioskServiceUnlock')
      await bootstrapAndroidKioskLockAtStartup()
    }
  }
  finally {
    platformReady = true
    resolvePlatformReady()
  }
}

/** Proxy that always delegates to the current platform instance. Use after initPlatform() on Android. */
export const platform: IPlatform = new Proxy({} as IPlatform, {
  get(_, prop: string) {
    return (getPlatformInstance() as unknown as Record<string, unknown>)[prop]
  }
})

export type { IConnectionStatus, IPlatform, IPlatformApp, IPlatformStorage, IPlatformUpdater, IPlatformWatchdog, IPlayerUpdatePayload, PlatformKind, PlayerPlatformHeader } from './types'
